import { SendEmailDto, emailType } from '@eventr/common'
import { FormInputText, MainButton } from '@eventr/common-storybook'
import { zodResolver } from '@hookform/resolvers/zod'
import { Stack } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { usePromiseTracker } from 'react-promise-tracker'
import { z } from 'zod'
import { EmailAPI } from '../../../data/email'
import { UserAPI } from '../../../data/User'
import i18n from '../../../i18n/i18next'
import { bs } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { getRedirectUrl } from '../../../utils'
import { reactAppStage } from '../../../utils/reactAppStage'
import { logEvent } from '../../firebase/firebase'
import { useGuestStore } from '../../guest/guest.store'
import { NavigationView } from '../../layout/NavigationView'
import { SocialLogins } from '../components/socialLogins'

const validationSchema = z.object({
    email: z.string().email(),
})

type ValidationSchema = z.infer<typeof validationSchema>

export function getUrlFromStage(email: string) {
    switch (reactAppStage()) {
        case 'local':
            return `http://localhost:3000/authentication/addDetails?email=${encodeURIComponent(
                email
            )}&stage=${reactAppStage()}`
        default:
            return `authentication/addDetails?email=${encodeURIComponent(
                email
            )}&stage=${reactAppStage()}`
    }
}

export function SignUp() {
    const { promiseInProgress } = usePromiseTracker()

    const methods = useForm<ValidationSchema>({
        resolver: zodResolver(validationSchema),
    })
    const { handleSubmit, control } = methods

    const onSubmit: SubmitHandler<ValidationSchema> = async (data) => {
        loadingSignal.value = true
        try {
            const userExists = await UserAPI.userExists(data?.email)
            if (!userExists) {
                const body: SendEmailDto = {
                    emailType: emailType.TEAMS_email_validation,
                    subject: 'Confirm email',
                    sendingTo: [data.email],
                    link: getRedirectUrl(getUrlFromStage(data.email)),
                }
                const resp = await EmailAPI.send(body)
                if (resp.status === 'error') {
                    defaultAddSnack(resp.message ?? "error sending email", 'error')
                    loadingSignal.value = false
                    return
                }
                logEvent('sign_up_started', {
                    email: data.email,
                    guestcopy: useGuestStore.getState().cloneOnSignUp,
                })
                loadingSignal.value = false
                window.pushPath('/authentication/emailConfirmation')
            } else {
                await bs().emailSignIn(data.email)
                loadingSignal.value = false
            }
        } catch {
            loadingSignal.value = false
            defaultAddSnack(i18n.t('snack.try-later'), 'error')
        }
    }
    function backButtonAction() {
        window.pushPath('/')
    }

    const emailTextField = (
        <FormInputText
            control={control}
            type="email"
            fieldLabel={i18n.t('general.email')}
            required
            name={'email'}
        />
    )
    const socialLogins = (
        <SocialLogins
            toptext={i18n.t('authentication.sign-up.or-sign-up-with')}
            onGoogleClick={() => bs().signInWithProvider('google')}
            onAppleClick={() => bs().signInWithProvider('apple')}
            onEntraClick={() => bs().signInWithProvider('entra')}
        ></SocialLogins>
    )

    const continueButton = (
        <MainButton
            style={{ width: '100%' }}
            loading={promiseInProgress}
            onClick={() => handleSubmit(onSubmit)()}
            text={i18n.t('general.continue')}
            id="continueButton"
        />
    )

    const footer = <Stack padding="16px">{continueButton}</Stack>

    return (
        <NavigationView
            navigationBarTitle={i18n.t('authentication.sign-up.sign-up')}
            navigationBarSubtitle={i18n.t('authentication.sign-up.enter-email')}
            navigationBarDisplayMode="large"
            usePageTransition
            backButtonAction={backButtonAction}
            footer={footer}
        >
            <FormProvider {...methods}>
                <Stack padding="16px">
                    {emailTextField}
                    {socialLogins}
                </Stack>
            </FormProvider>
        </NavigationView>
    )
}
